import { useCallback, useState } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';

import { ActivePracticeSession } from '../types';
import {
  getNextExerciseInPracticeSessionQuery,
  GetNextExerciseInPracticeSessionQueryResult,
  GetNextExerciseInPracticeSessionQueryVariables,
} from '../graphql/queries/getNextExerciseInPracticeSessionQuery';

import {
  answerPracticeSessionExerciseMutation,
  AnswerPracticeSessionExerciseMutationResult,
  AnswerPracticeSessionExerciseMutationVariables,
} from '../graphql/mutations/answerPracticeSessionExerciseMutation';

export interface DoPracticeSessionActionsHookInput {
  courseId: string;
  practiceSessionId: string;
}

export interface DoPracticeSessionActionsHookPayload {
  // getNextExercise: () => void;
  getNextExerciseLoading: boolean;
  practiceSession?: ActivePracticeSession;
  currentExercise?: any;
  selectedOptions?: Record<string, any>;

  isCurrentExerciseFinished: boolean;
  isPracticeSessionFinished: boolean;

  onSelectQuestionOption: (option: string, questionId: string) => void;
  sendExerciseAnswer: () => void;
  submitting: boolean;

  onContinue: () => void;
}

export default function usePracticeSession(input: DoPracticeSessionActionsHookInput): DoPracticeSessionActionsHookPayload {
  const { courseId, practiceSessionId } = input;
  const [selectedOptions, setSelectedOptions] = useState<Record<string, any>>({});

  const { showErrorNotification } = useSnackbarNotifications();

  /**
   * On select question option
   */
  const onSelectQuestionOption = useCallback((option: any, questionId: string) => {
    setSelectedOptions((state) => ({ ...state, [questionId]: option }));
  }, []);

  /**
   * Get next exercise
   */

  const {
    data,
    loading: getNextExerciseLoading,
    refetch: getNextExercise,
  } = useQuery<GetNextExerciseInPracticeSessionQueryResult, GetNextExerciseInPracticeSessionQueryVariables>(getNextExerciseInPracticeSessionQuery, {
    variables: { courseId, practiceSessionId },
    // fetchPolicy: 'network-only',
    onError: showErrorNotification,
  });
  const activePracticeSession = data?.activePracticeSession;
  const { currentExercise, currentExerciseIndex, exercisesCount } = activePracticeSession || {};
  const isCurrentExerciseFinished = Boolean(currentExercise?.finishedAt);
  const isPracticeSessionFinished = isCurrentExerciseFinished && currentExerciseIndex === exercisesCount;

  const handleGetNextExercise = useCallback(() => {
    if (isPracticeSessionFinished) {
      return;
    }
    getNextExercise();
  }, [isPracticeSessionFinished, getNextExercise]);

  /**
   * Send practice session answer
   */
  const [sendExerciseAnswer, { loading: submitting }] = useMutation<
    AnswerPracticeSessionExerciseMutationResult,
    AnswerPracticeSessionExerciseMutationVariables
  >(answerPracticeSessionExerciseMutation, { onError: showErrorNotification });
  const onSendExerciseAnswer = useCallback(() => {
    if (!currentExercise) {
      return;
    }
    sendExerciseAnswer({
      variables: {
        courseId,
        practiceSessionId,
        input: {
          exercise: currentExercise._id,
          selectedOptions: selectedOptions,
        },
      },
    });
  }, [sendExerciseAnswer, courseId, practiceSessionId, currentExercise, selectedOptions]);

  /**
   * On Continue
   */
  const onContinue = useCallback(() => {
    if (isCurrentExerciseFinished) {
      handleGetNextExercise();
    } else {
      onSendExerciseAnswer();
    }
  }, [handleGetNextExercise, onSendExerciseAnswer, isCurrentExerciseFinished]);

  return {
    // getNextExercise: handleGetNextExercise,
    getNextExerciseLoading,

    isCurrentExerciseFinished,
    isPracticeSessionFinished,
    practiceSession: activePracticeSession,
    currentExercise,
    submitting,
    selectedOptions,

    sendExerciseAnswer: onSendExerciseAnswer,
    onContinue,
    onSelectQuestionOption,
  };
}
